import { GroupSecurity, getReferenceId } from '@quromedical/fhir-common';
import type { AdmissionGetResponse } from '@quromedical/models';
import type { Fn } from '@quromedical/utils';
import { admissionServiceDisplay } from 'core/display';
import { router } from 'expo-router';
import { strings } from 'strings';

import type { Action } from './types';

interface GetPatientActionParams {
  parentId: string;
  patientId: string;
  patientName: string;
  /**
   * The default KBar priority is 1
   */
  priority?: number;
}

export const getAdmissionAction = (
  admission: AdmissionGetResponse,
): Action | undefined => {
  const reference = getReferenceId(admission.patient);

  if (!reference) {
    return undefined;
  }

  return {
    parent: 'patient',
    id: reference,
    permission: GroupSecurity.PatientRead,
    name: admission.patient?.display || '',
    hidden: !admission.patient?.display,
    subtitle: admission.service
      ? admissionServiceDisplay[admission.service]
      : undefined,
    iconName: 'person-outline',
  };
};

export const getPatientActions: Fn<GetPatientActionParams, Action[]> = ({
  parentId,
  patientId,
  patientName,
}) => {
  const common = {
    parent: parentId,
    subtitle: patientName,
  };

  return [
    {
      ...common,
      id: `${patientId}-profile`,
      name: strings.KBarActiveAdmissionActionProfileTitle,
      iconName: 'person-outline',
      permission: GroupSecurity.PatientRead,
      shortcut: ['p', 'p'],
      perform: () =>
        router.push({
          pathname: `/patient/${patientId}/profile`,
        }),
    },
    {
      ...common,
      id: `${patientId}-vitals`,
      name: strings.KBarActiveAdmissionActionVitalsTitle,
      iconName: 'multiline-chart',
      permission: GroupSecurity.ClinicalImpressionRead,
      shortcut: ['p', 'v'],
      perform: () =>
        router.push({
          pathname: `/patient/${patientId}/vitals`,
        }),
    },
    {
      ...common,
      id: `${patientId}-clinical`,
      name: strings.KBarActiveAdmissionActionClinicalTitle,
      iconName: 'pages',
      permission: GroupSecurity.ClinicalImpressionRead,
      shortcut: ['p', 'c'],
      perform: () =>
        router.push({
          pathname: `/patient/${patientId}/clinical`,
        }),
    },
    {
      ...common,
      id: `${patientId}-notes`,
      name: strings.KBarActiveAdmissionActionNotesTitle,
      iconName: 'notes',
      permission: GroupSecurity.ClinicalImpressionRead,
      shortcut: ['p', 'n'],
      perform: () =>
        router.push({
          pathname: `/patient/${patientId}/notes`,
        }),
    },
    {
      ...common,
      id: `${patientId}-pathology`,
      name: strings.KBarActiveAdmissionActionPathologyTitle,
      iconName: 'science',
      permission: GroupSecurity.ServiceRequestRead,
      shortcut: ['p', 't'],
      perform: () =>
        router.push({
          pathname: `/patient/${patientId}/service-request`,
        }),
    },
    {
      ...common,
      id: `${patientId}-media`,
      name: strings.KBarActiveAdmissionActionMediaTitle,
      iconName: 'attach-file',
      permission: GroupSecurity.MediaRead,
      shortcut: ['p', 'm'],
      perform: () =>
        router.push({
          pathname: `/patient/${patientId}/media`,
        }),
    },
    {
      ...common,
      id: `${patientId}-video-consult`,
      name: strings.KBarActiveAdmissionActionVideoConsultTitle,
      iconName: 'video-call',
      permission: GroupSecurity.VideoConsultCreate,
      shortcut: ['p', 'v', 'c'],
      perform: () =>
        router.push({
          pathname: `/patient/${patientId}/communication`,
        }),
    },
  ];
};
